// CR038: Community Signal Builder Component (adapted from Flyer CandidateSignalBuilder)

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import type { CommunitySignal, CommunitySignalType, CommunitySignalParams } from '@/lib/community-signals/types';

interface CommunitySignalBuilderProps {
  signals: CommunitySignal[];
  onChange: (signals: CommunitySignal[]) => void;
  disabled?: boolean;
}

const SIGNAL_TYPES: { value: CommunitySignalType; label: string; description: string }[] = [
  { value: 'KEYWORD', label: 'Keyword', description: 'Match specific terms' },
  { value: 'REGEX', label: 'Regex', description: 'Pattern matching' },
  { value: 'PROXIMITY', label: 'Proximity', description: 'Terms near each other' },
  { value: 'NEGATIVE_KEYWORDS', label: 'Negative Keywords', description: 'Reduce score when found' },
  { value: 'URL_MATCH', label: 'URL Match', description: 'Match URL patterns' },
  { value: 'METADATA', label: 'Metadata', description: 'Score by upvotes/comments' },
];

function generateSignalId(): string {
  return `sig_${Date.now().toString(36)}`;
}

function getDefaultParams(type: CommunitySignalType): CommunitySignalParams {
  switch (type) {
    case 'KEYWORD':
      return { terms: [''], match_mode: 'ANY', case_sensitive: false };
    case 'REGEX':
      return { pattern: '', min_matches: 1 };
    case 'PROXIMITY':
      return { anchor_terms: [''], near_terms: [''], window_chars: 80 };
    case 'NEGATIVE_KEYWORDS':
      return { terms: [''], mode: 'SUBTRACT' };
    case 'URL_MATCH':
      return { patterns: [''], match_mode: 'ANY' };
    case 'METADATA':
      return { field: 'upvotes', operator: 'gte', value: 10 };
    default:
      return { terms: [''], match_mode: 'ANY', case_sensitive: false };
  }
}

interface SignalEditorProps {
  signal: CommunitySignal | null;
  onSave: (signal: CommunitySignal) => void;
  onCancel: () => void;
}

function SignalEditor({ signal, onSave, onCancel }: SignalEditorProps) {
  const [editedSignal, setEditedSignal] = useState<CommunitySignal>(
    signal || {
      id: generateSignalId(),
      name: '',
      type: 'KEYWORD',
      enabled: true,
      weight: 3,
      params: getDefaultParams('KEYWORD'),
    }
  );

  const updateParam = (key: string, value: unknown) => {
    setEditedSignal({
      ...editedSignal,
      params: { ...editedSignal.params, [key]: value } as CommunitySignalParams,
    });
  };

  const params = editedSignal.params as unknown as Record<string, unknown>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input
            value={editedSignal.name}
            onChange={(e) => setEditedSignal({ ...editedSignal, name: e.target.value })}
            placeholder="Signal name"
          />
        </div>
        <div className="space-y-2">
          <Label>Type</Label>
          <Select
            value={editedSignal.type}
            onValueChange={(v) =>
              setEditedSignal({
                ...editedSignal,
                type: v as CommunitySignalType,
                params: getDefaultParams(v as CommunitySignalType),
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SIGNAL_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Weight</Label>
        <Input
          type="number"
          value={editedSignal.weight}
          onChange={(e) => setEditedSignal({ ...editedSignal, weight: parseFloat(e.target.value) || 0 })}
        />
        <p className="text-xs text-muted-foreground">
          Positive = increases score. Negative = decreases score.
        </p>
      </div>

      {/* Type-specific params */}
      {editedSignal.type === 'KEYWORD' && (
        <div className="space-y-2">
          <Label>Terms (comma-separated)</Label>
          <Input
            value={(params.terms as string[])?.join(', ') || ''}
            onChange={(e) => updateParam('terms', e.target.value.split(',').map((t) => t.trim()))}
            placeholder="gift card, giftcard, gc"
          />
          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center gap-2">
              <Switch
                checked={params.case_sensitive as boolean}
                onCheckedChange={(v) => updateParam('case_sensitive', v)}
              />
              <Label className="text-xs">Case sensitive</Label>
            </div>
            <Select
              value={(params.match_mode as string) || 'ANY'}
              onValueChange={(v) => updateParam('match_mode', v)}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY">Any</SelectItem>
                <SelectItem value="ALL">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {editedSignal.type === 'REGEX' && (
        <div className="space-y-2">
          <Label>Pattern</Label>
          <Input
            value={(params.pattern as string) || ''}
            onChange={(e) => updateParam('pattern', e.target.value)}
            placeholder="\\$\\s*\\d{1,4}"
          />
          <div className="space-y-1">
            <Label>Min Matches</Label>
            <Input
              type="number"
              min={1}
              value={(params.min_matches as number) || 1}
              onChange={(e) => updateParam('min_matches', parseInt(e.target.value) || 1)}
            />
          </div>
        </div>
      )}

      {editedSignal.type === 'PROXIMITY' && (
        <div className="space-y-2">
          <Label>Anchor Terms (comma-separated)</Label>
          <Input
            value={(params.anchor_terms as string[])?.join(', ') || ''}
            onChange={(e) => updateParam('anchor_terms', e.target.value.split(',').map((t) => t.trim()))}
            placeholder="gift card"
          />
          <Label>Near Terms (comma-separated)</Label>
          <Input
            value={(params.near_terms as string[])?.join(', ') || ''}
            onChange={(e) => updateParam('near_terms', e.target.value.split(',').map((t) => t.trim()))}
            placeholder="buy, purchase, spend"
          />
          <div className="space-y-1">
            <Label>Window (chars)</Label>
            <Input
              type="number"
              min={10}
              max={500}
              value={(params.window_chars as number) || 80}
              onChange={(e) => updateParam('window_chars', parseInt(e.target.value) || 80)}
            />
          </div>
        </div>
      )}

      {editedSignal.type === 'NEGATIVE_KEYWORDS' && (
        <div className="space-y-2">
          <Label>Terms (comma-separated)</Label>
          <Input
            value={(params.terms as string[])?.join(', ') || ''}
            onChange={(e) => updateParam('terms', e.target.value.split(',').map((t) => t.trim()))}
            placeholder="giveaway, contest, expired"
          />
          <Select
            value={(params.mode as string) || 'SUBTRACT'}
            onValueChange={(v) => updateParam('mode', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SUBTRACT">Subtract (reduce score)</SelectItem>
              <SelectItem value="HARD_BLOCK">Hard Block (disqualify)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {editedSignal.type === 'URL_MATCH' && (
        <div className="space-y-2">
          <Label>URL Patterns (comma-separated)</Label>
          <Input
            value={(params.patterns as string[])?.join(', ') || ''}
            onChange={(e) => updateParam('patterns', e.target.value.split(',').map((t) => t.trim()))}
            placeholder="redflagdeals.com, reddit.com/r/giftcards"
          />
          <Select
            value={(params.match_mode as string) || 'ANY'}
            onValueChange={(v) => updateParam('match_mode', v)}
          >
            <SelectTrigger className="w-24 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ANY">Any</SelectItem>
              <SelectItem value="ALL">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {editedSignal.type === 'METADATA' && (
        <div className="space-y-2">
          <Label>Field</Label>
          <Select
            value={(params.field as string) || 'upvotes'}
            onValueChange={(v) => updateParam('field', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upvotes">Upvotes</SelectItem>
              <SelectItem value="comments">Comments</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select
              value={(params.operator as string) || 'gte'}
              onValueChange={(v) => updateParam('operator', v)}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gt">&gt;</SelectItem>
                <SelectItem value="gte">≥</SelectItem>
                <SelectItem value="lt">&lt;</SelectItem>
                <SelectItem value="lte">≤</SelectItem>
                <SelectItem value="eq">=</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={(params.value as number) || 0}
              onChange={(e) => updateParam('value', parseInt(e.target.value) || 0)}
              className="w-24"
            />
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave(editedSignal)}>Save Signal</Button>
      </DialogFooter>
    </div>
  );
}

export function CommunitySignalBuilder({ signals, onChange, disabled }: CommunitySignalBuilderProps) {
  const [editingSignal, setEditingSignal] = useState<CommunitySignal | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleToggle = (signalId: string, enabled: boolean) => {
    onChange(signals.map((s) => (s.id === signalId ? { ...s, enabled } : s)));
  };

  const handleDelete = (signalId: string) => {
    onChange(signals.filter((s) => s.id !== signalId));
  };

  const handleSave = (signal: CommunitySignal) => {
    const exists = signals.find((s) => s.id === signal.id);
    if (exists) {
      onChange(signals.map((s) => (s.id === signal.id ? signal : s)));
    } else {
      onChange([...signals, signal]);
    }
    setEditingSignal(null);
    setIsDialogOpen(false);
  };

  const getTypeBadgeColor = (type: CommunitySignalType) => {
    switch (type) {
      case 'KEYWORD': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'REGEX': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'PROXIMITY': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'NEGATIVE_KEYWORDS': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'URL_MATCH': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'METADATA': return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8"></TableHead>
            <TableHead className="w-10">On</TableHead>
            <TableHead>Name</TableHead>
            <TableHead className="w-28">Type</TableHead>
            <TableHead className="w-16 text-right">Weight</TableHead>
            <TableHead className="w-20"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {signals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                No signals configured. Add one to start scoring candidates.
              </TableCell>
            </TableRow>
          ) : signals.map((signal) => (
            <TableRow key={signal.id} className={!signal.enabled ? 'opacity-50' : ''}>
              <TableCell>
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
              </TableCell>
              <TableCell>
                <Switch
                  checked={signal.enabled}
                  onCheckedChange={(v) => handleToggle(signal.id, v)}
                  disabled={disabled}
                />
              </TableCell>
              <TableCell className="font-medium text-xs">{signal.name}</TableCell>
              <TableCell>
                <Badge variant="secondary" className={`text-xs ${getTypeBadgeColor(signal.type)}`}>
                  {signal.type}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <span className={signal.weight < 0 ? 'text-destructive' : 'text-green-600'}>
                  {signal.weight > 0 ? '+' : ''}{signal.weight}
                </span>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => { setEditingSignal(signal); setIsDialogOpen(true); }}
                    disabled={disabled}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(signal.id)}
                    disabled={disabled}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setEditingSignal(null)}
            disabled={disabled}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Signal
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSignal ? 'Edit Signal' : 'Add Signal'}</DialogTitle>
          </DialogHeader>
          <SignalEditor
            signal={editingSignal}
            onSave={handleSave}
            onCancel={() => { setEditingSignal(null); setIsDialogOpen(false); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
